import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, switchMap } from 'rxjs/operators';
import { of } from 'rxjs';
import { LoaderService } from '@app/services/loader.service';
import {
  LogsList,
  LogsListSuccess,
  LogsListError,
  LogsListPaginated,
  LogsListPaginatedSuccess,
  LogsListPaginatedError,
  LogGet,
  LogGetSuccess,
  LogGetError,
  LogDelete,
  LogDeleteSuccess,
  LogDeleteError
} from './logs.actions';
import { ILogsState } from './logs.state';
import { LogService } from '@app/services/log.service';

@Injectable()
export class LogsEffects {
  private _actions$ = inject(Actions);
  private _store = inject<Store<ILogsState>>(Store);
  private logService = inject(LogService);
  private loaderService = inject(LoaderService);


  logsList$ = createEffect(() => this._actions$.pipe(
    ofType(LogsList),
    switchMap((action) => {
      this.loaderService.show();
      return this.logService.getAll().pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return LogsListSuccess({ logs: resp.data.list });
          }
          this.loaderService.hide(true);
          return LogsListError({ error: this.loaderService.getErrorMessage(resp) });
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(LogsListError({ error: this.loaderService.getErrorMessage(error) }));
        })
      );
    })
  ));

  logsListPaginated$ = createEffect(() => this._actions$.pipe(
    ofType(LogsListPaginated),
    switchMap((action: any) => {
      this.loaderService.show();
      console.log('LogsListPaginated effect - action:', action);
      // Extract params from action (action itself contains the params, not action.params)
      const { type, ...params } = action;
      console.log('Extracted params:', params);
      return this.logService.getAll(params).pipe(
        map((resp: any) => {
          console.log('LogService response:', resp);
          if (resp.success) {
            this.loaderService.hide();
            console.log('Dispatching LogsListPaginatedSuccess with:', resp.data);
            return LogsListPaginatedSuccess({ paginatedLogs: resp.data });
          }
          this.loaderService.hide(true);
          console.error('LogService error:', resp);
          return LogsListPaginatedError({ error: this.loaderService.getErrorMessage(resp) });
        }),
        catchError(error => {
          this.loaderService.hide(true);
          console.error('LogService catchError:', error);
          return of(LogsListPaginatedError({ error: this.loaderService.getErrorMessage(error) }));
        })
      );
    })
  ));

  logGet$ = createEffect(() => this._actions$.pipe(
    ofType(LogGet),
    switchMap((action) => {
      this.loaderService.show();
      return this.logService.getById(action.logId).pipe(
        map((resp: any) => {
          if (resp.success && resp.data) {
            this.loaderService.hide();
            return LogGetSuccess({ log: resp.data });
          }
          this.loaderService.hide(true);
          return LogGetError({ error: 'Log not found' });
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(LogGetError({ error: this.loaderService.getErrorMessage(error) }));
        })
      );
    })
  ));

  logDelete$ = createEffect(() => this._actions$.pipe(
    ofType(LogDelete),
    switchMap((action) => {
      this.loaderService.show();
      return this.logService.deleteLog(action.logId).pipe(
        map((resp: any) => {
          if (resp.success) {
            this.loaderService.hide();
            return LogDeleteSuccess({ success: 'Log deleted successfully' });
          }
          this.loaderService.hide(true);
          return LogDeleteError({ error: this.loaderService.getErrorMessage(resp) });
        }),
        catchError(error => {
          this.loaderService.hide(true);
          return of(LogDeleteError({ error: this.loaderService.getErrorMessage(error) }));
        })
      );
    })
  ));
}
